import React from "react";
import { Box, Typography, TextField, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { reportsNumberInputSx } from "./reportsFilterStyles";

const OPERATOR_LABELS = { gt: "MORE THAN", lt: "LESS THAN", between: "BETWEEN" };

// Matches CategoryDurationFilterChip's own segmented MORE THAN/LESS THAN/
// BETWEEN look exactly (same numbers, copied over) -- Rahim's explicit ask,
// 2026-08-17: this was a tiny unlabeled >/</<-> toggle, reading as a
// completely different control language from its own sibling component.
const segmentedTrackSx = {
  display: "flex",
  width: "100%",
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)"),
  borderRadius: "10px",
  p: 0.4,
  mb: 1.25,
  "& .MuiToggleButton-root": {
    flex: 1,
    border: "none",
    borderRadius: "8px !important",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    color: "text.secondary",
    py: 0.7,
    "&.Mui-selected": {
      bgcolor: "background.paper",
      color: "text.primary",
      boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
    },
    "&.Mui-selected:hover": { bgcolor: "background.paper" },
  },
};

const fieldLabelSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: 0.5,
};

// Short, fixed-width -- NOT fullWidth (Rahim's ask, 2026-08-17: "the value
// input was way too long" when it stretched to fill the row). Widened
// slightly from the original 64px (Rahim's ask, same day, once the tick
// button next to it was removed for all 3 Time filters -- more room to
// give the input now that nothing else needs to share this row).
const durationInputSx = {
  width: 84,
  flexShrink: 0,
  "& .MuiOutlinedInput-root": { fontSize: "0.85rem" },
  "& input": { padding: "6.5px 8px", textAlign: "center" },
  ...reportsNumberInputSx,
};

// Shared by Response/Resolution/First Response Time + Response/Resolution/
// First Response Count column-header dropdowns (ReportsTable.jsx, Overview
// table only -- CategoryDurationFilterChip is a separate component with its
// own staged/Cancel-Apply popover for the category drill-down).
//
// `unit`: a real duration filter passes e.g. "Minutes" -- labels the value
// field "Duration (Minutes)". No unit PICKER at all (Rahim's explicit ask:
// never offer a unit choice, it's always minutes here) -- the confirm tick
// takes that spot instead, via `confirmButton` below. Omit `unit` for a
// plain count filter (Response Count etc.), which has no unit concept --
// the field just uses `label` instead.
//
// `confirmButton`: the tick IconButton JSX, owned by the caller (each of
// the 6 usages in ReportsTable.jsx needs its own onClick -- some dispatch
// fetchReports, some just close the popover) -- rendered INLINE next to
// the now-short value input instead of on its own row below.
const DurationOperatorFilter = ({ label, hideLabel, unit, operator, onOperatorChange, value1, onValue1Change, value2, onValue2Change, confirmButton }) => (
  <Box sx={{ width: "100%" }}>
    {!hideLabel && <Typography sx={fieldLabelSx}>{label}</Typography>}
    <ToggleButtonGroup
      size="small"
      exclusive
      fullWidth
      value={operator}
      onChange={(_, newOp) => onOperatorChange(newOp)}
      sx={segmentedTrackSx}
    >
      {Object.entries(OPERATOR_LABELS).map(([val, text]) => (
        <ToggleButton key={val} value={val}>
          {text}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>

    {(operator === "gt" || operator === "lt") && (
      <Box>
        <Typography sx={fieldLabelSx}>{unit ? `Duration (${unit})` : label}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <TextField
            size="small"
            type="number"
            placeholder="value"
            value={value1}
            onChange={(e) => onValue1Change(e.target.value)}
            sx={durationInputSx}
            inputProps={{ min: 0 }}
          />
          {confirmButton}
        </Box>
      </Box>
    )}
    {operator === "between" && (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box>
            <Typography sx={fieldLabelSx}>From</Typography>
            <TextField
              size="small"
              type="number"
              placeholder="from"
              value={value1}
              onChange={(e) => onValue1Change(e.target.value)}
              sx={durationInputSx}
              inputProps={{ min: 0 }}
            />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>To</Typography>
            <TextField
              size="small"
              type="number"
              placeholder="to"
              value={value2}
              onChange={(e) => onValue2Change(e.target.value)}
              sx={durationInputSx}
              inputProps={{ min: 0 }}
            />
          </Box>
          <Box sx={{ alignSelf: "flex-end" }}>{confirmButton}</Box>
        </Box>
      </Box>
    )}
  </Box>
);

export default DurationOperatorFilter;
